import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Shield } from 'lucide-react';
import { createRecord, getRecordsForObject, updateRecord } from '../../services/recordStorageService';
import { logRecordCreated, logStatusChanged } from '../../services/activityLogService';
import { persistedTaskRecords } from '../../services/mockData/tasks';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { SearchableSelect } from '../modals/SearchableSelect';
import { StandardButton } from '../standard';
import { useCreditBlockClients, searchCreditBlockClientsViaTraversal } from '../../services/queries/creditBlockQueries';
import { createUnlockRequestRecordsForClient } from '../../services/queries/creditBlockBureauQueries';
import type { CreditBlockClient } from '../../types';
import { mergeClientsByPickId, resolveClientPickId } from '../../services/creditBlock/creditBlockClientPick';
import { BUREAU_LOGOS, getBureauLogo } from '../../constants/bureauLogos';
import { buildCreditBlockCaseCreatePayload, CREDIT_BLOCK_CASE_ORIGINS } from '../../services/creditBlock/creditBlockCaseCreate';
import { CREDIT_BLOCK_SYSTEM_CASE_ORIGIN } from '../../services/creditBlock/caseOriginDisplay';
import {
  applyCreditBlockRecordTypeFields,
  findCreditBlockRecordTypeTarget,
} from '../../services/creditBlock/creditBlockCaseGraphRelations';
import {
  getNewCaseKindConfig,
  type NewCaseRequestKind,
} from '../../data/creditBlockNewCaseKindConfig';

import { shouldUseGobodo } from '../../utils/gobodoUtils';
import GobodoConfig from '../../config/gobodoConfig';
import {
  usersToCreditBlockAssigneeOptions,
} from '../../services/gobodo/creditBlockNewCaseGobodoSearch';
import { fetchUsersForUserManagement, userKeys } from '../../services/queries/userQueries';
import {
  CREDIT_BLOCK_ASSIGNEE_USER_FILTERS,
  decodeNameToken,
  isCreditBlockTeamUser,
  ownerSelectOptionsForAssignee,
  parseOwnerPick,
} from '../../services/creditBlock/creditBlockAssigneePickerUtils';

/** UC9: cancel open tasks for the same client when an extension is filed. */
async function cancelExistingOpenTasksForClient(
  clientId: string,
  newCaseId: string,
  actorUserId: string,
  actorUserName: string
): Promise<number> {
  let cancelled = 0;
  try {
    if (shouldUseGobodo()) {
      const allTasks = await getRecordsForObject('Task__c', { size: 200 }).catch(() => []);
      for (const t of allTasks as Record<string, unknown>[]) {
        const st = String(t.status ?? t.Status__c ?? '');
        if (st === 'Completed' || st === 'Cancelled') continue;
        const desc = String(t.Description__c ?? t.description ?? '');
        const relCase = String(t.RelatedTo__c ?? '');
        const matchesClient = desc.toLowerCase().includes(clientId.toLowerCase()) || desc.toLowerCase().includes('client:');
        if (!matchesClient && relCase === newCaseId) continue;
        if (!matchesClient) continue;
        const taskId = String(t.id ?? (t as { key?: string }).key ?? '');
        if (!taskId) continue;
        const reason = `Superseded by extension request (Case ${newCaseId})`;
        await updateRecord(
          'Task__c',
          taskId,
          { status: 'Cancelled', Status__c: 'Cancelled', CancellationReason__c: reason },
          { gobodoPropertyPatch: true }
        ).catch(() => {});
        logStatusChanged(taskId, 'Task__c', st, 'Cancelled', actorUserId, actorUserName);
        cancelled++;
      }
    } else {
      const allTasks = persistedTaskRecords.get();
      const updated = allTasks.map((t) => {
        const st = String(t.status ?? '');
        if (st === 'Completed' || st === 'Cancelled') return t;
        const desc = String((t as Record<string, unknown>).Description__c ?? (t as Record<string, unknown>).description ?? '');
        if (!desc.toLowerCase().includes(clientId.toLowerCase())) return t;
        const taskId = String(t.id);
        const reason = `Superseded by extension request (Case ${newCaseId})`;
        logStatusChanged(taskId, 'Task__c', st, 'Cancelled', actorUserId, actorUserName);
        cancelled++;
        return { ...t, status: 'Cancelled', cancellationReason: reason };
      });
      persistedTaskRecords.set(updated);
    }
  } catch {
    // best-effort
  }
  return cancelled;
}

const CB_BUREAUS = ['equifax', 'experian', 'transunion', 'chexsystems'] as const;
const CB_CORE_THREE = ['equifax', 'experian', 'transunion'] as const;
const CB_BUREAU_LABELS: Record<string, string> = {
  chexsystems: 'ChexSystems',
  equifax: 'Equifax',
  experian: 'Experian',
  transunion: 'TransUnion',
};

const REQUEST_KIND_OPTIONS: { value: NewCaseRequestKind; label: string }[] = [
  { value: 'unlock_request', label: 'Unlock / Unblock' },
  { value: 'lock_request', label: 'Lock / Block' },
  { value: 'permanent_unblock', label: 'Permanent Unblock' },
  { value: 'extension', label: 'Extension' },
  { value: 'special_case', label: 'Special Case' },
];

type BureauKey = (typeof CB_BUREAUS)[number];

function deriveCaseSubType(kind: NewCaseRequestKind, selected: Set<BureauKey>): string {
  if (kind === 'permanent_unblock') return 'Permanent unblock';
  if (kind === 'extension') return 'Extension';
  if (kind === 'special_case') return 'Special case';
  const suffix = kind === 'lock_request' ? 'Lock / Block' : 'Unblock';
  const isAllThreeCore =
    CB_CORE_THREE.every((b) => selected.has(b)) && !selected.has('chexsystems');
  if (isAllThreeCore) return `All 3 Bureaus ${suffix}`;
  const ordered = CB_BUREAUS.filter((b) => selected.has(b));
  if (ordered.length === 0) return suffix;
  const labels = ordered.map((b) => CB_BUREAU_LABELS[b]);
  return `${labels.join(', ')} ${suffix}`;
}

function clientSelectOptions(clients: CreditBlockClient[]): { value: string; label: string }[] {
  const seen = new Set<string>();
  const out: { value: string; label: string }[] = [];
  for (const c of clients) {
    const id = resolveClientPickId(c);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const name = String(c.accountName ?? '').trim() || id;
    const key = c.arrayInfo?.clientKey?.trim();
    const label = key && key !== name ? `${name} · ${key}` : name;
    out.push({ value: id, label });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export interface CreditBlockNewCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (caseId: string) => void;
  /** Current Clients-tab page (optional) — merged so the picker isn’t empty before the full-list query finishes. */
  extraClients?: CreditBlockClient[];
}

export const CreditBlockNewCaseModal: React.FC<CreditBlockNewCaseModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  extraClients = [],
}) => {
  const { userId, userName } = useCurrentUser();
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  const [clientId, setClientId] = useState('');
  const [ownerPick, setOwnerPick] = useState('');
  const [subject, setSubject] = useState('');
  const [requestKind, setRequestKind] = useState<NewCaseRequestKind>('unlock_request');
  const [selectedBureaus, setSelectedBureaus] = useState<Set<BureauKey>>(new Set<BureauKey>(['equifax']));
  const [specialCaseNoBureau, setSpecialCaseNoBureau] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [origin, setOrigin] = useState<string>(CREDIT_BLOCK_SYSTEM_CASE_ORIGIN);
  const [description, setDescription] = useState('');
  const [clientSearchRaw, setClientSearchRaw] = useState('');
  const [assigneeSearchRaw, setAssigneeSearchRaw] = useState('');
  const debouncedClientSearch = useDebouncedValue(clientSearchRaw, 250);
  const debouncedAssigneeSearch = useDebouncedValue(assigneeSearchRaw, 250);

  const gobodoGraphId = useMemo(() => GobodoConfig.graph(), []);

  const derivedSubType = useMemo(
    () => deriveCaseSubType(requestKind, selectedBureaus),
    [requestKind, selectedBureaus]
  );

  const kindConfig = useMemo(() => getNewCaseKindConfig(requestKind), [requestKind]);

  const useGobodo = shouldUseGobodo();

  // Gobodo: skip loading the full traversal+join list here — use `extraClients` (Clients-tab pool) + traversal search while typing.
  // Off-Gobodo: load persisted/mock clients as before.
  const {
    data: cbClients = [],
    isLoading: clientsLoading,
    isFetching: clientsFetching,
    isError: clientsError,
    error: clientsErrorDetail,
  } = useCreditBlockClients(!useGobodo);

  const mergedClients = useMemo(
    () => mergeClientsByPickId(extraClients ?? [], cbClients),
    [extraClients, cbClients]
  );

  /** Same source as Admin → Users home (`useUsers` with no filters). */
  const { data: adminUsers = [], isLoading: adminUsersLoading } = useQuery({
    queryKey: userKeys.list(CREDIT_BLOCK_ASSIGNEE_USER_FILTERS),
    queryFn: () => fetchUsersForUserManagement(CREDIT_BLOCK_ASSIGNEE_USER_FILTERS),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const creditBlockTeamUsers = useMemo(() => {
    const scoped = adminUsers.filter(isCreditBlockTeamUser);
    return scoped.length > 0 ? scoped : adminUsers;
  }, [adminUsers]);

  const adminUserPickerOptions = useMemo(
    () => usersToCreditBlockAssigneeOptions(creditBlockTeamUsers),
    [creditBlockTeamUsers]
  );

  const { data: clientTraversalClients = [], isFetching: clientTraversalFetching } = useQuery({
    queryKey: ['credit-block', 'new-case-modal', 'client-traversal', gobodoGraphId, debouncedClientSearch],
    queryFn: () => searchCreditBlockClientsViaTraversal(debouncedClientSearch),
    enabled: useGobodo && isOpen && debouncedClientSearch.trim().length > 0,
    staleTime: 60_000,
  });

  const clientRemoteOptions = useMemo(
    () => clientSelectOptions(clientTraversalClients),
    [clientTraversalClients]
  );

  const localClientRows = useMemo(() => clientSelectOptions(mergedClients), [mergedClients]);

  /** While Gobodo search is active, list must be traversal hits only — merging `localClientRows` + skipLocalFilter would show the full extraClients pool (unrelated names) sorted A–Z. */
  const clientRows = useMemo(() => {
    if (!useGobodo) return localClientRows;
    const q = debouncedClientSearch.trim();
    if (q.length === 0) return localClientRows;
    const remote = [...clientRemoteOptions].sort((a, b) => a.label.localeCompare(b.label));
    if (clientId && !remote.some((o) => o.value === clientId)) {
      const selected = localClientRows.find((o) => o.value === clientId);
      if (selected) return [selected, ...remote];
    }
    return remote;
  }, [useGobodo, localClientRows, clientRemoteOptions, debouncedClientSearch, clientId]);

  const clientAccountSearchPending =
    useGobodo &&
    clientSearchRaw.trim().length > 0 &&
    clientSearchRaw.trim() !== debouncedClientSearch.trim();

  const assigneePickerOptions = useMemo(() => {
    const q = debouncedAssigneeSearch.trim().toLowerCase();
    if (!q) return adminUserPickerOptions;
    return adminUserPickerOptions.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [debouncedAssigneeSearch, adminUserPickerOptions]);

  const ownersLoading = adminUsersLoading;

  const ownerRows = useMemo(
    () => ownerSelectOptionsForAssignee(assigneePickerOptions, userName),
    [assigneePickerOptions, userName]
  );

  useEffect(() => {
    if (!isOpen) return;
    const rk: NewCaseRequestKind = 'unlock_request';
    const defaultBureaus = new Set<BureauKey>(['equifax' as BureauKey]);
    const cfg = getNewCaseKindConfig(rk);
    setClientId('');
    setRequestKind(rk);
    setSelectedBureaus(defaultBureaus);
    setSpecialCaseNoBureau(false);
    setStartDate('');
    setEndDate('');
    setReason('');
    setDescription('');
    setClientSearchRaw('');
    setAssigneeSearchRaw('');
    setPriority(cfg.defaultPriority);
    setOrigin(cfg.defaultOrigin);
    setSubject(deriveCaseSubType(rk, defaultBureaus));
  }, [isOpen]);

  useEffect(() => {
    const cfg = getNewCaseKindConfig(requestKind);
    setPriority(cfg.defaultPriority);
    setOrigin(cfg.defaultOrigin);
    if (!cfg.showStartDate) setStartDate('');
    if (!cfg.showEndDate) setEndDate('');
    if (requestKind !== 'special_case') setSpecialCaseNoBureau(false);
  }, [requestKind]);

  useEffect(() => {
    if (!isOpen || ownerRows.length === 0) return;
    setOwnerPick((prev) => {
      if (prev && ownerRows.some((o) => o.value === prev)) return prev;
      const you = ownerRows.find(
        (o) =>
          o.label.toLowerCase().includes('(you)') ||
          o.label.trim().toLowerCase() === userName.trim().toLowerCase() ||
          decodeNameToken(o.value) === userName.trim()
      );
      return (you ?? ownerRows[0]).value;
    });
  }, [isOpen, ownerRows, userName]);

  const selectedClientLabel = useMemo(() => {
    const o = clientRows.find((c) => c.value === clientId);
    return o?.label?.split(' · ')[0]?.trim() ?? '';
  }, [clientRows, clientId]);

  const createMutation = useMutation({
    mutationFn: async (): Promise<{
      caseRec: { id: string };
      historyError: string | null;
      caseSnapshot: Record<string, unknown>;
    }> => {
      const kindCfg = getNewCaseKindConfig(requestKind);
      if (!clientId.trim()) throw new Error('Select a client.');
      if (!subject.trim()) throw new Error('Subject is required.');
      if (kindCfg.startDateRequired && !startDate?.trim()) {
        throw new Error('Start date is required for this request type.');
      }
      if (kindCfg.endDateRequired && !endDate?.trim()) {
        throw new Error('End date is required for this request type.');
      }
      if (kindCfg.reasonRequired && !reason.trim()) {
        throw new Error('Reason is required for this request type.');
      }

      const { ownerUserId, ownerName } = ownerPick ? parseOwnerPick(ownerPick, ownerRows) : { ownerUserId: undefined, ownerName: '' };
      const name = ownerName.trim();

      const sub = derivedSubType;
      const descParts = [description.trim(), reason.trim() && `Bureau request: ${reason.trim()}`].filter(Boolean);
      const payload = buildCreditBlockCaseCreatePayload({
        clientId: clientId.trim(),
        clientName: selectedClientLabel || clientId.trim(),
        owner: name,
        ownerUserId,
        subject: subject.trim(),
        subType: sub,
        priority,
        origin,
        description: descParts.join('\n\n'),
      });
      const recordTypeTarget = useGobodo ? await findCreditBlockRecordTypeTarget() : null;
      if (useGobodo) {
        applyCreditBlockRecordTypeFields(payload, recordTypeTarget);
      }
      const caseRec = await createRecord('Case__c', payload as Record<string, unknown>);
      const caseId = String(caseRec.id);

      if (requestKind === 'extension') {
        await cancelExistingOpenTasksForClient(clientId.trim(), caseId, userId, userName);
      }

      const bureausForHistory =
        requestKind === 'special_case' && specialCaseNoBureau
          ? []
          : Array.from(selectedBureaus);

      const historyErrParts: string[] = [];
      for (const b of bureausForHistory) {
        try {
          await createUnlockRequestRecordsForClient(clientId.trim(), {
            bureau: b,
            requestType: requestKind,
            reason: [reason.trim(), `Case: ${caseId}`].filter(Boolean).join(' · '),
            startDate: kindCfg.showStartDate && startDate?.trim() ? startDate.trim() : undefined,
            endDate: kindCfg.showEndDate && endDate?.trim() ? endDate.trim() : undefined,
            caseId,
          });
        } catch (e) {
          historyErrParts.push(`${CB_BUREAU_LABELS[b]}: ${e instanceof Error ? e.message : 'Failed'}`);
        }
      }
      const historyError = historyErrParts.length > 0 ? historyErrParts.join(' · ') : null;

      return {
        caseRec: { id: caseId },
        historyError,
        caseSnapshot: {
          id: caseId,
          caseNumber: caseId,
          subject: subject.trim(),
          status: 'New',
          priority,
          type: 'Credit Block',
          subType: sub,
          owner: name,
          origin,
          accountId: clientId.trim(),
          accountName: selectedClientLabel || clientId.trim(),
          createdDate: new Date().toISOString(),
        },
      };
    },
    onSuccess: ({ caseRec, historyError, caseSnapshot }) => {
      const id = caseRec.id;
      logRecordCreated(id, 'Case__c', userId, userName, {
        creditBlockCaseIntake: true,
        clientId,
        ownerPick,
        requestKind,
        selectedBureaus:
          requestKind === 'special_case' && specialCaseNoBureau
            ? ['none']
            : Array.from(selectedBureaus),
        derivedSubType,
        origin: 'Credit Block header form',
      });
      if (historyError) {
        showError('Case created — bureau requests failed', historyError);
      } else {
        const bureauCount = requestKind === 'special_case' && specialCaseNoBureau ? 0 : selectedBureaus.size;
        success(
          'Case and bureau request(s) created',
          bureauCount === 0
            ? `Special case created · ${subject.trim()}`
            : `${bureauCount} bureau request(s) · ${subject.trim()}`
        );
      }
      // Optimistically prepend the new case into cached paginated queries so it shows instantly
      queryClient.setQueriesData(
        { queryKey: ['credit-block', 'cases', 'paginated'] },
        (old: any) => {
          if (!old?.cases) return old;
          const exists = old.cases.some((c: any) => c.id === caseSnapshot.id);
          if (exists) return old;
          return { ...old, cases: [caseSnapshot, ...old.cases], totalCount: (old.totalCount ?? 0) + 1 };
        }
      );
      queryClient.invalidateQueries({ queryKey: ['gobodo', 'records', 'Task__c'] });
      queryClient.invalidateQueries({ queryKey: ['gobodo', 'records', 'Task'] });
      queryClient.invalidateQueries({ queryKey: ['gobodo', 'records', 'Case__c'] });
      queryClient.invalidateQueries({ queryKey: ['gobodo', 'records', 'Case'] });
      queryClient.invalidateQueries({ queryKey: ['gobodo', 'records'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'clients'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'unlock-requests'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'case-unlock-requests'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'bureaus', clientId.trim()] });
      queryClient.invalidateQueries({ queryKey: ['caseListViews'] });
      queryClient.invalidateQueries({ queryKey: ['caseRecords'] });
      queryClient.invalidateQueries({ queryKey: ['caseListViewData'] });
      queryClient.invalidateQueries({ queryKey: ['genericListViews', 'data', 'Case__c'] });
      onClose();
      onCreated?.(id);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Could not create case.';
      showError('Create failed', msg);
    },
  });

  const selectClass =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';

  const canSubmit = useMemo(() => {
    if (!clientId.trim() || !subject.trim()) return false;
    if (!(requestKind === 'special_case' && specialCaseNoBureau) && selectedBureaus.size === 0) return false;
    if (kindConfig.startDateRequired && !startDate?.trim()) return false;
    if (kindConfig.endDateRequired && !endDate?.trim()) return false;
    if (kindConfig.reasonRequired && !reason.trim()) return false;
    return true;
  }, [
    clientId,
    subject,
    requestKind,
    specialCaseNoBureau,
    selectedBureaus.size,
    kindConfig.startDateRequired,
    kindConfig.endDateRequired,
    kindConfig.reasonRequired,
    startDate,
    endDate,
    reason,
  ]);

  if (!isOpen) return null;

  const clientsHardErrMsg =
    clientsError && clientRows.length === 0
      ? clientsErrorDetail instanceof Error
        ? clientsErrorDetail.message
        : 'Could not load Credit Block clients.'
      : null;
  const clientsSoftWarnMsg =
    clientsError && clientRows.length > 0
      ? 'Full account list failed to refresh; you can still pick from accounts loaded for this session.'
      : null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cb-new-case-title"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
          <h2 id="cb-new-case-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            New Credit Block case
          </h2>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {clientsHardErrMsg && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
              {clientsHardErrMsg}
            </div>
          )}
          {clientsSoftWarnMsg && (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 px-3 py-2 text-sm text-blue-900 dark:text-blue-200">
              {clientsSoftWarnMsg}
            </div>
          )}

          <div>
            <SearchableSelect
              label="Account (client)"
              name="clientId"
              value={clientId}
              onChange={setClientId}
              options={clientRows}
              skipLocalFilter={useGobodo}
              onSearchTermChange={useGobodo ? setClientSearchRaw : undefined}
              placeholder={
                clientsLoading && clientRows.length === 0
                  ? 'Loading accounts…'
                  : clientsFetching && clientRows.length === 0
                    ? 'Loading accounts…'
                    : useGobodo && (clientAccountSearchPending || (clientTraversalFetching && debouncedClientSearch.trim().length > 0))
                      ? 'Searching accounts…'
                      : 'Search by name or client key…'
              }
              required
              disabled={false}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Same Credit Block→Client traversal search as the Clients tab. Recent tab results appear when the field is empty; type to search the graph.
            </p>
          </div>

          <SearchableSelect
            label="Case owner (assignee)"
            name="owner"
            value={ownerPick}
            onChange={setOwnerPick}
            options={ownerRows}
            skipLocalFilter
            onSearchTermChange={setAssigneeSearchRaw}
            placeholder={
              ownersLoading && ownerRows.length === 0
                ? 'Loading users…'
                : 'Search Credit Block team users…'
            }
            required={false}
            disabled={false}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
            Optional — leave unassigned to route to the team queue for a manager to assign later.
          </p>

          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
            Same flow as client detail <strong className="font-medium text-gray-700 dark:text-gray-300">New bureau request</strong>
            — case type stays <strong className="font-medium">Credit Block</strong>; picks set sub-type and Gobodo <strong className="font-medium">Unlock_Request</strong> rows.
          </p>

          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Request type</span>
            <div className="flex flex-col gap-1" role="group" aria-label="Request type">
              {REQUEST_KIND_OPTIONS.map((opt) => {
                const selected = requestKind === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setRequestKind(opt.value);
                      if (opt.value !== 'special_case') setSpecialCaseNoBureau(false);
                      setSubject(deriveCaseSubType(opt.value, selectedBureaus));
                    }}
                    className={`flex w-full items-center rounded-md border px-3 py-2 text-sm text-left font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/25 dark:border-blue-400 text-gray-900 dark:text-gray-100'
                        : 'border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {kindConfig.hint && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{kindConfig.hint}</p>
            )}
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Bureau <span className="font-normal text-gray-500 dark:text-gray-400">(select one or more)</span>
            </span>
            <div className="flex flex-col gap-1" role="group" aria-label="Bureau">
              <button
                type="button"
                onClick={() => {
                  const allThree = new Set<BureauKey>(Array.from(CB_CORE_THREE) as BureauKey[]);
                  setSelectedBureaus(allThree);
                  setSpecialCaseNoBureau(false);
                  setSubject(deriveCaseSubType(requestKind, allThree));
                }}
                className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
                  CB_CORE_THREE.every((b) => selectedBureaus.has(b)) && selectedBureaus.size === 3
                    ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/25 dark:border-blue-400'
                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }`}
              >
                <div className="min-w-0 text-left">
                  <span className="font-medium text-gray-900 dark:text-gray-100">All 3 bureaus</span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400 tabular-nums">EQ · EX · TU</span>
                </div>
                <div className="flex shrink-0 items-center justify-end gap-0.5" aria-hidden>
                  {CB_CORE_THREE.map((b) => (
                    <img
                      key={b}
                      src={BUREAU_LOGOS[b]}
                      alt=""
                      className="h-[0.949rem] w-[1.898rem] object-contain object-right opacity-90 dark:opacity-95"
                    />
                  ))}
                </div>
              </button>
              {CB_BUREAUS.map((b) => {
                const src = getBureauLogo(b);
                const checked = selectedBureaus.has(b);
                return (
                  <button
                    key={b}
                    type="button"
                    onClick={() => {
                      setSelectedBureaus((prev) => {
                        const next = new Set(prev);
                        if (next.has(b)) {
                          next.delete(b);
                          if (next.size === 0) next.add(b);
                        } else {
                          next.add(b);
                        }
                        setSubject(deriveCaseSubType(requestKind, next));
                        return next;
                      });
                      setSpecialCaseNoBureau(false);
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
                      checked
                        ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/25 dark:border-blue-400'
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0 text-left">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        checked
                          ? 'bg-blue-600 border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                          : 'border-gray-300 dark:border-gray-500'
                      }`}>
                        {checked && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{CB_BUREAU_LABELS[b]}</span>
                    </div>
                    <div className="flex h-[1.265rem] min-w-[3.45rem] shrink-0 items-center justify-end" aria-hidden>
                      {src ? (
                        <img src={src} alt="" className="max-h-[0.949rem] max-w-[3.795rem] object-contain object-right" />
                      ) : (
                        <Shield className="h-[0.949rem] w-[0.949rem] text-gray-500 dark:text-gray-400" />
                      )}
                    </div>
                  </button>
                );
              })}
              {requestKind === 'special_case' && (
                <button
                  type="button"
                  onClick={() => {
                    setSpecialCaseNoBureau(true);
                    setSelectedBureaus(new Set<BureauKey>());
                    setSubject(deriveCaseSubType('special_case', new Set<BureauKey>()));
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
                    specialCaseNoBureau
                      ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/25 dark:border-blue-400'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0 text-left">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                      specialCaseNoBureau
                        ? 'bg-blue-600 border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                        : 'border-gray-300 dark:border-gray-500'
                    }`}>
                      {specialCaseNoBureau && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">Not applicable / Unrelated</span>
                  </div>
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              Select multiple bureaus. ChexSystems is not included in &quot;All 3&quot;; select it separately if needed.
              {requestKind === 'special_case' ? ' Special Case can also be created with "Not applicable / Unrelated".' : ''}
            </p>
          </div>

          {(kindConfig.showStartDate || kindConfig.showEndDate) && (
            <div
              className={`grid gap-3 ${kindConfig.showStartDate && kindConfig.showEndDate ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}
            >
              {kindConfig.showStartDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start date
                    {kindConfig.startDateRequired && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={selectClass}
                  />
                </div>
              )}
              {kindConfig.showEndDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End date
                    {kindConfig.endDateRequired && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={selectClass}
                  />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason
              {kindConfig.reasonRequired && <span className="text-red-500"> *</span>}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className={selectClass}
              placeholder={
                kindConfig.reasonRequired
                  ? 'Required — appears on Unlock_Request and the case'
                  : 'Optional — appears on Unlock_Request and the case'
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={selectClass}
              placeholder="Defaults from request type + bureau; edit if needed"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Sub-type on the case still follows request type + bureau above; this field is the title shown in case lists (you
              can customize it).
            </p>
          </div>

          <details className="group rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-900/40">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 list-none [&::-webkit-details-marker]:hidden flex items-center justify-between">
              <span>Priority, origin &amp; extra description</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400 group-open:hidden">Show</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400 hidden group-open:inline">Hide</span>
            </summary>
            <div className="px-3 pb-3 pt-0 space-y-3 border-t border-gray-200 dark:border-gray-600">
              <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
                Defaults update when you change request type ({priority} · {origin}). Adjust here if needed.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                  <select value={priority} onChange={(e) => setPriority(e.target.value)} className={selectClass}>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Case origin</label>
                  <select value={origin} onChange={(e) => setOrigin(e.target.value)} className={selectClass}>
                    {CREDIT_BLOCK_CASE_ORIGINS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className={selectClass}
                  placeholder="Optional — appended to the case (separate from Reason)"
                />
              </div>
            </div>
          </details>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3">
          <StandardButton variant="ghost" type="button" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !canSubmit}
          >
            {createMutation.isPending ? 'Creating…' : 'Create case'}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
